import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DEFAULTS = {
  logo_base64: "",
  accent_color: "#FFEB00",
  portal_title: "Digital Impressions",
  login_tagline: "",
};

const BrandContext = createContext({ branding: DEFAULTS, scope: "agency", refresh: () => {} });

export const useBrand = () => useContext(BrandContext);

// Convert "#FFEB00" → "230, 212, 0" (a slightly darker hover, same hue).
const darken = (hex, amt = 18) => {
  try {
    const h = hex.replace("#", "");
    const r = Math.max(0, parseInt(h.slice(0, 2), 16) - amt);
    const g = Math.max(0, parseInt(h.slice(2, 4), 16) - amt);
    const b = Math.max(0, parseInt(h.slice(4, 6), 16) - amt);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  } catch {
    return hex;
  }
};

const applyToDom = (b) => {
  const root = document.documentElement;
  if (b?.accent_color) {
    root.style.setProperty("--accent", b.accent_color);
    root.style.setProperty("--accent-hover", darken(b.accent_color));
    root.style.setProperty("--border-focus", b.accent_color);
  } else {
    root.style.removeProperty("--accent");
    root.style.removeProperty("--accent-hover");
    root.style.removeProperty("--border-focus");
  }
  // Browser tab title
  if (b?.portal_title) {
    document.title = b.portal_title;
  }
  // Favicon — if a logo is set, use it. Otherwise fall back to default.
  try {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b?.logo_base64 || "/digital-impressions-logo.png";
  } catch { /* ignore */ }
};

export const BrandProvider = ({ children }) => {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);
  const [scope, setScope] = useState("agency");

  const refresh = useCallback(async () => {
    // If logged in, fetch the effective (per-user) brand. Else, fetch agency
    // default for the login page (no auth header required — but endpoint
    // requires auth, so login page just uses DEFAULTS).
    if (!user) {
      setBranding(DEFAULTS);
      setScope("agency");
      applyToDom(DEFAULTS);
      return;
    }
    try {
      const res = await axios.get(`${API}/branding/effective`);
      const b = { ...DEFAULTS, ...(res.data?.branding || {}) };
      setBranding(b);
      setScope(res.data?.scope || "agency");
      applyToDom(b);
    } catch {
      setBranding(DEFAULTS);
      setScope("agency");
      applyToDom(DEFAULTS);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ branding, scope, refresh }), [branding, scope, refresh]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};
